import { createEmptyPartyDrafts, normalizePartySlots } from '@/features/events/builder';
import { createTemplatePartyLabels, normalizeTemplateBuilderDraft } from '@/features/templates/builder';
import { supabase } from '@/lib/supabase/client';
import { deleteTemplateRecordRpc } from '@/lib/supabase/rpc';
import type { Database, Json } from '@/types/database';
import type {
  EventBuilderPartyDraft,
  TemplateBuilderDraft,
  TemplatePartyLabel,
  TemplateDeleteResult,
  TemplateDraftRecord,
} from '@/types/domain';

type TemplateInsert = Database['public']['Tables']['party_templates']['Insert'];
type TemplateUpdate = Database['public']['Tables']['party_templates']['Update'];
type TemplateSlotInsert = Database['public']['Tables']['party_template_slots']['Insert'];
type TemplateSlotUpdate = Database['public']['Tables']['party_template_slots']['Update'];
type TemplateDraftInsert = Database['public']['Tables']['template_drafts']['Insert'];
type TemplateDraftUpdate = Database['public']['Tables']['template_drafts']['Update'];
type TemplateDraftRow = Database['public']['Tables']['template_drafts']['Row'];
type TemplateRow = Database['public']['Tables']['party_templates']['Row'];
type TemplateSlotRow = Database['public']['Tables']['party_template_slots']['Row'];

export interface TemplateWorkingDraftPayload {
  draftId?: string;
  templateId?: string;
  sourceTemplateId?: string;
  sourceEventId?: string;
  templateUpdatedAtSnapshot?: string;
  draft: TemplateBuilderDraft;
}

export interface SavedTemplateBuilderResult {
  templateId: string;
  draft: TemplateBuilderDraft;
  savedAt: string;
}

function createClientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function assertSupabase() {
  if (!supabase) {
    throw new Error('Supabase is not configured.');
  }

  return supabase;
}

function normalizeParties(parties: EventBuilderPartyDraft[]): EventBuilderPartyDraft[] {
  const defaults = createEmptyPartyDrafts();

  return defaults.map((defaultParty) => {
    const party = parties.find((entry) => entry.partyNumber === defaultParty.partyNumber) ?? defaultParty;

    return {
      ...party,
      label: party.label.trim() || defaultParty.label,
      sortOrder: defaultParty.partyNumber,
      slots: normalizePartySlots(
        [...party.slots].sort((left, right) => left.slotOrder - right.slotOrder).map((slot) => ({
          ...slot,
          partyNumber: defaultParty.partyNumber,
        })),
      ),
    };
  });
}

function toTemplateInsertValues(draft: TemplateBuilderDraft, actorProfileId: string): TemplateInsert {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    is_active: draft.isActive,
    party_labels_json: JSON.parse(JSON.stringify(createTemplatePartyLabels(draft.parties))) as Json,
    archived_at: draft.isActive ? null : new Date().toISOString(),
    archived_by: draft.isActive ? null : actorProfileId,
    created_by: actorProfileId,
  };
}

function toTemplateUpdateValues(draft: TemplateBuilderDraft, actorProfileId: string): TemplateUpdate {
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    is_active: draft.isActive,
    party_labels_json: JSON.parse(JSON.stringify(createTemplatePartyLabels(draft.parties))) as Json,
    archived_at: draft.isActive ? null : new Date().toISOString(),
    archived_by: draft.isActive ? null : actorProfileId,
  };
}

function toTemplateSlotValues(
  slot: EventBuilderPartyDraft['slots'][number],
  templateId: string,
): TemplateSlotInsert & TemplateSlotUpdate {
  return {
    template_id: templateId,
    party_number: slot.partyNumber,
    slot_order: slot.slotOrder,
    role_name: slot.roleCategory,
    slot_note: slot.note.trim() || null,
    role_category: slot.roleCategory,
    weapon_catalog_id: slot.weaponId ?? null,
    weapon_name: slot.weaponName.trim(),
    weapon_image_path: slot.imagePath.trim(),
    capacity: slot.capacity,
    waitlist_enabled: slot.waitlistEnabled,
    is_active: slot.isActive,
  };
}

function serializeDraftParties(parties: EventBuilderPartyDraft[]): Json {
  return JSON.parse(JSON.stringify(parties)) as Json;
}

function mapPartyLabels(value: Json | null | undefined): TemplatePartyLabel[] {
  if (!Array.isArray(value)) {
    return createEmptyPartyDrafts().map((party) => ({
      partyNumber: party.partyNumber,
      label: party.label,
      sortOrder: party.sortOrder,
    }));
  }

  return createEmptyPartyDrafts().map((defaultParty) => {
    const entry = value.find((item) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        return false;
      }

      const record = item as Record<string, Json>;
      return Number(record.party_number ?? record.partyNumber) === defaultParty.partyNumber;
    });

    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return {
        partyNumber: defaultParty.partyNumber,
        label: defaultParty.label,
        sortOrder: defaultParty.sortOrder,
      };
    }

    const record = entry as Record<string, Json>;

    return {
      partyNumber: defaultParty.partyNumber,
      label:
        (typeof record.label === 'string' && record.label.trim()) ||
        defaultParty.label,
      sortOrder:
        typeof record.sort_order === 'number'
          ? record.sort_order
          : typeof record.sortOrder === 'number'
            ? record.sortOrder
            : defaultParty.sortOrder,
    };
  });
}

function buildSavedTemplateDraft(template: TemplateRow, slots: TemplateSlotRow[]): TemplateBuilderDraft {
  const partyLabels = mapPartyLabels(template.party_labels_json);

  return normalizeTemplateBuilderDraft({
    id: template.id,
    name: template.name,
    description: template.description ?? '',
    isActive: template.is_active,
    parties: createEmptyPartyDrafts().map((defaultParty) => {
      const savedParty = partyLabels.find((entry) => entry.partyNumber === defaultParty.partyNumber);

      return {
        ...defaultParty,
        label: savedParty?.label ?? defaultParty.label,
        sortOrder: savedParty?.sortOrder ?? defaultParty.sortOrder,
        slots: slots
          .filter((slot) => slot.party_number === defaultParty.partyNumber)
          .sort((left, right) => left.slot_order - right.slot_order)
          .map((slot) => ({
            clientId: createClientId(),
            id: slot.id,
            partyNumber: slot.party_number,
            slotOrder: slot.slot_order,
            note: slot.slot_note ?? '',
            roleCategory: slot.role_category,
            weaponId: slot.weapon_catalog_id ?? undefined,
            weaponName: slot.weapon_name,
            imagePath: slot.weapon_image_path,
            capacity: slot.capacity,
            waitlistEnabled: slot.waitlist_enabled,
            isActive: slot.is_active,
          })),
      };
    }),
  });
}

async function hydrateSavedTemplateResult(templateId: string): Promise<SavedTemplateBuilderResult> {
  const client = assertSupabase();
  const [{ data: template, error: templateError }, { data: slots, error: slotsError }] = await Promise.all([
    client.from('party_templates').select('*').eq('id', templateId).single(),
    client
      .from('party_template_slots')
      .select('*')
      .eq('template_id', templateId)
      .order('party_number', { ascending: true })
      .order('slot_order', { ascending: true }),
  ]);

  if (templateError || !template) {
    throw new Error(templateError?.message ?? 'Failed to reload the saved template.');
  }

  if (slotsError) {
    throw new Error(slotsError.message);
  }

  return {
    templateId: template.id,
    draft: buildSavedTemplateDraft(template, slots ?? []),
    savedAt: template.updated_at,
  };
}

function mapTemplateDraftRow(row: TemplateDraftRow): TemplateDraftRecord {
  const normalized = normalizeTemplateBuilderDraft({
    id: row.template_id ?? undefined,
    name: row.name,
    description: row.description ?? '',
    isActive: row.is_active,
    parties: Array.isArray(row.parties_json)
      ? (row.parties_json as unknown as TemplateBuilderDraft['parties'])
      : createEmptyPartyDrafts(),
  });

  return {
    id: row.id,
    ownerProfileId: row.owner_profile_id,
    templateId: row.template_id ?? undefined,
    sourceTemplateId: row.source_template_id ?? undefined,
    sourceEventId: row.source_event_id ?? undefined,
    name: row.name,
    description: row.description ?? '',
    isActive: row.is_active,
    parties: normalized.parties,
    templateUpdatedAtSnapshot: row.template_updated_at_snapshot ?? undefined,
    lastAutosavedAt: row.last_autosaved_at,
    lastRestoredAt: row.last_restored_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

async function getExistingTemplateDraft(actorProfileId: string, templateId?: string): Promise<TemplateDraftRow | null> {
  const client = assertSupabase();
  const query = client
    .from('template_drafts')
    .select('*')
    .eq('owner_profile_id', actorProfileId)
    .order('updated_at', { ascending: false })
    .limit(1);

  const scopedQuery = templateId ? query.eq('template_id', templateId) : query.is('template_id', null);
  const { data, error } = await scopedQuery.maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return data ?? null;
}

async function syncTemplateSlots(templateId: string, parties: EventBuilderPartyDraft[]): Promise<void> {
  const client = assertSupabase();
  const { data: existingSlots, error: existingError } = await client
    .from('party_template_slots')
    .select('id')
    .eq('template_id', templateId);

  if (existingError) {
    throw new Error(existingError.message);
  }

  const existingSlotIds = new Set((existingSlots ?? []).map((slot) => slot.id));
  const nextSlots = parties.flatMap((party) => party.slots);
  const nextIds = new Set(nextSlots.map((slot) => slot.id).filter((id): id is string => Boolean(id)));
  const removableIds = (existingSlots ?? []).filter((slot) => !nextIds.has(slot.id)).map((slot) => slot.id);

  if (removableIds.length > 0) {
    const { data: usedEventSlots, error: eventSlotError } = await client
      .from('event_slots')
      .select('template_slot_id')
      .in('template_slot_id', removableIds);

    if (eventSlotError) {
      throw new Error(eventSlotError.message);
    }

    const protectedIds = new Set((usedEventSlots ?? []).map((slot) => slot.template_slot_id).filter((id): id is string => Boolean(id)));
    const inactiveIds = removableIds.filter((id) => protectedIds.has(id));
    const deleteIds = removableIds.filter((id) => !protectedIds.has(id));

    if (inactiveIds.length > 0) {
      const { error } = await client.from('party_template_slots').update({ is_active: false }).in('id', inactiveIds);

      if (error) {
        throw new Error(error.message);
      }
    }

    if (deleteIds.length > 0) {
      const { error } = await client.from('party_template_slots').delete().in('id', deleteIds);

      if (error) {
        throw new Error(error.message);
      }
    }
  }

  for (const party of parties) {
    for (const slot of party.slots) {
      const values = toTemplateSlotValues(slot, templateId);

      if (slot.id && existingSlotIds.has(slot.id)) {
        const { error } = await client.from('party_template_slots').update(values).eq('id', slot.id);

        if (error) {
          throw new Error(error.message);
        }

        continue;
      }

      const { error } = await client.from('party_template_slots').insert(values);

      if (error) {
        throw new Error(error.message);
      }
    }
  }
}

export async function loadTemplateWorkingDraft(actorProfileId: string, templateId?: string): Promise<TemplateDraftRecord | null> {
  const existing = await getExistingTemplateDraft(actorProfileId, templateId);
  return existing ? mapTemplateDraftRow(existing) : null;
}

export async function saveTemplateWorkingDraft(
  actorProfileId: string,
  payload: TemplateWorkingDraftPayload,
): Promise<TemplateDraftRecord> {
  const client = assertSupabase();
  const normalizedDraft = normalizeTemplateBuilderDraft(payload.draft);
  const values: TemplateDraftInsert & TemplateDraftUpdate = {
    owner_profile_id: actorProfileId,
    template_id: payload.templateId ?? null,
    source_template_id: payload.sourceTemplateId ?? null,
    source_event_id: payload.sourceEventId ?? null,
    name: normalizedDraft.name.trim(),
    description: normalizedDraft.description.trim() || null,
    is_active: normalizedDraft.isActive,
    parties_json: serializeDraftParties(normalizedDraft.parties),
    template_updated_at_snapshot: payload.templateUpdatedAtSnapshot ?? null,
    last_autosaved_at: new Date().toISOString(),
  };

  const existingDraft =
    payload.draftId
      ? await client.from('template_drafts').select('*').eq('id', payload.draftId).eq('owner_profile_id', actorProfileId).maybeSingle().then(({ data, error }) => {
          if (error) {
            throw new Error(error.message);
          }

          return data ?? null;
        })
      : await getExistingTemplateDraft(actorProfileId, payload.templateId);

  if (existingDraft) {
    const { data, error } = await client
      .from('template_drafts')
      .update(values)
      .eq('id', existingDraft.id)
      .select('*')
      .single();

    if (error || !data) {
      throw new Error(error?.message ?? 'Failed to update the working draft.');
    }

    return mapTemplateDraftRow(data);
  }

  const { data, error } = await client.from('template_drafts').insert(values).select('*').single();

  if (error || !data) {
    throw new Error(error?.message ?? 'Failed to create the working draft.');
  }

  return mapTemplateDraftRow(data);
}

export async function markTemplateWorkingDraftRestored(draftId: string): Promise<void> {
  const client = assertSupabase();
  const { error } = await client
    .from('template_drafts')
    .update({ last_restored_at: new Date().toISOString() })
    .eq('id', draftId);

  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteTemplateWorkingDraft(actorProfileId: string, templateId?: string): Promise<void> {
  const client = assertSupabase();
  const existingDraft = await getExistingTemplateDraft(actorProfileId, templateId);

  if (!existingDraft) {
    return;
  }

  const { error } = await client.from('template_drafts').delete().eq('id', existingDraft.id);

  if (error) {
    throw new Error(error.message);
  }
}

export async function saveTemplateBuilderDraft(
  draft: TemplateBuilderDraft,
  actorProfileId: string,
  options?: {
    createNew?: boolean;
  },
): Promise<SavedTemplateBuilderResult> {
  const client = assertSupabase();
  const normalizedDraft: TemplateBuilderDraft = {
    ...normalizeTemplateBuilderDraft(draft),
    parties: normalizeParties(draft.parties),
  };

  if (!options?.createNew && normalizedDraft.id) {
    const { error } = await client
      .from('party_templates')
      .update(toTemplateUpdateValues(normalizedDraft, actorProfileId))
      .eq('id', normalizedDraft.id);

    if (error) {
      throw new Error(error.message);
    }

    await syncTemplateSlots(normalizedDraft.id, normalizedDraft.parties);
    return hydrateSavedTemplateResult(normalizedDraft.id);
  }

  const { data, error } = await client
    .from('party_templates')
    .insert(toTemplateInsertValues(normalizedDraft, actorProfileId))
    .select('id')
    .single();

  if (error || !data) {
    throw new Error(error?.message ?? 'Failed to create template.');
  }

  try {
    await syncTemplateSlots(data.id, normalizedDraft.parties);
    return hydrateSavedTemplateResult(data.id);
  } catch (saveError) {
    await client.from('party_templates').delete().eq('id', data.id);
    throw saveError;
  }
}

export async function setTemplateActiveState(templateId: string, actorProfileId: string, isActive: boolean): Promise<void> {
  const client = assertSupabase();
  const { error } = await client
    .from('party_templates')
    .update({
      is_active: isActive,
      archived_at: isActive ? null : new Date().toISOString(),
      archived_by: isActive ? null : actorProfileId,
    })
    .eq('id', templateId);

  if (error) {
    throw new Error(error.message);
  }
}

export async function deleteTemplateRecord(templateId: string, confirmationText: string): Promise<TemplateDeleteResult> {
  const { data, error } = await deleteTemplateRecordRpc(templateId, confirmationText);

  if (error) {
    throw new Error(error.message);
  }

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Unexpected template deletion response.');
  }

  return {
    templateId: typeof data.template_id === 'string' ? data.template_id : templateId,
    result: data.result === 'deactivated' ? 'deactivated' : 'deleted',
    reason: typeof data.reason === 'string' ? data.reason : undefined,
  };
}
